/**
 * Shared API client.
 *
 * Centralises:
 *   - The base URL (with trailing-slash normalization).
 *   - Bearer token attachment from the auth store (no React imports
 *     here — `useAuthStore.getState()` is a pure read).
 *   - 401 handling: clear the session and let the caller surface a
 *     "please sign in" message instead of a stale token retry loop.
 *   - Network error normalization into the standard envelope shape
 *     so callers don't need a try/catch around every call.
 *
 * Existing feature-level clients (e.g. `auth/api.ts`,
 * `checkout/api.ts`, `prompts/api.ts`) are kept intact — we use this
 * client for new protected endpoints (enrollments, progress sync,
 * comments, account updates) without forcing a sweeping rewrite.
 */

import type { ApiResponse } from '@abdallah-issa/shared'
import { useAuthStore } from '@/features/auth/store'

const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

/**
 * Single-flight refresh. When several requests 401 at once we only want
 * ONE call to /auth/refresh; the rest await the same promise.
 */
let refreshInFlight: Promise<boolean> | null = null

async function tryRefreshToken(): Promise<boolean> {
  if (!API_URL) return false
  const { refreshToken, setTokens, clear } = useAuthStore.getState()
  if (!refreshToken) return false

  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ refreshToken }),
        })
        const json = (await res.json().catch(() => ({}))) as {
          success?: boolean
          data?: { token: string; refreshToken?: string }
        }
        if (res.ok && json.success && json.data?.token) {
          setTokens({ token: json.data.token, refreshToken: json.data.refreshToken })
          return true
        }
        // Refresh failed — the session is truly dead.
        clear()
        return false
      } catch {
        return false
      } finally {
        refreshInFlight = null
      }
    })()
  }
  return refreshInFlight
}

interface RequestOptions {
  /** Treat as authenticated; attach bearer + clear session on 401. */
  auth?: boolean
  /** AbortSignal for cancellation. */
  signal?: AbortSignal
  /** JSON body for POST/PUT/PATCH. */
  body?: unknown
  /** Extra headers (will not override Authorization or Content-Type). */
  headers?: Record<string, string>
}

function buildHeaders(opts: RequestOptions): HeadersInit {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(opts.headers ?? {}),
  }
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
  if (opts.auth) {
    const token = useAuthStore.getState().token
    if (token) headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  opts: RequestOptions = {},
  _isRetry = false,
): Promise<ApiResponse<T>> {
  if (!API_URL) {
    return {
      success: false,
      error: { code: 'OFFLINE', message: 'الـ API غير متاح في وضع المعاينة' },
    }
  }
  try {
    const res = await fetch(`${API_URL}${path.startsWith('/') ? '' : '/'}${path}`, {
      method,
      headers: buildHeaders(opts),
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    })

    if (res.status === 401 && opts.auth) {
      // Access token likely expired. Try a one-time silent refresh and
      // replay the request. Only clear the session if refresh fails.
      if (!_isRetry) {
        const refreshed = await tryRefreshToken()
        if (refreshed) {
          return request<T>(method, path, opts, true)
        }
      }
      // Refresh unavailable or failed — clear so the UI flips to
      // "signed out" and we don't replay a dead token.
      useAuthStore.getState().clear()
    }

    const json = (await res.json().catch(() => ({}))) as ApiResponse<T>
    if (!('success' in json)) {
      return {
        success: false,
        error: {
          code: 'BAD_RESPONSE',
          message: 'استجابة غير متوقعة من الخادم',
        },
      }
    }
    return json
  } catch (err) {
    if (opts.signal?.aborted) {
      throw err
    }
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'تعذّر الاتصال بالخادم',
        details: err instanceof Error ? err.message : String(err),
      },
    }
  }
}

/**
 * Raw `fetch` with bearer attachment, one silent refresh on 401 and a replay.
 *
 * `api.*` returns a parsed envelope, which cannot express FormData uploads or
 * SSE streams. Features that need those were hand-rolling their own
 * `Authorization` header straight from localStorage with no refresh, so once
 * the short-lived access token expired every call 401'd forever while the
 * navbar still showed the user as signed in. Use this instead.
 *
 * Returns the raw `Response` — the caller owns parsing.
 */
export async function authorizedFetch(
  url: string,
  init: RequestInit = {},
  opts: { auth?: boolean } = {},
): Promise<Response> {
  const auth = opts.auth ?? true
  const send = (token: string | null) => {
    const headers = new Headers(init.headers ?? {})
    // Never set Content-Type here: callers may be sending FormData, where the
    // browser must generate the multipart boundary itself.
    if (token) headers.set('Authorization', `Bearer ${token}`)
    else headers.delete('Authorization')
    return fetch(url, { ...init, headers })
  }

  const token = auth ? useAuthStore.getState().token : null
  const res = await send(token)
  if (res.status !== 401 || !auth) return res

  const refreshed = await tryRefreshToken()
  if (!refreshed) {
    useAuthStore.getState().clear()
    return res
  }
  return send(useAuthStore.getState().token)
}

/** Exposed so callers outside this module can trigger the single-flight refresh. */
export { tryRefreshToken as refreshAuthToken }

export const api = {
  get<T>(path: string, opts: Omit<RequestOptions, 'body'> = {}) {
    return request<T>('GET', path, opts)
  },
  post<T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'body'> = {}) {
    return request<T>('POST', path, { ...opts, body })
  },
  put<T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'body'> = {}) {
    return request<T>('PUT', path, { ...opts, body })
  },
  patch<T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'body'> = {}) {
    return request<T>('PATCH', path, { ...opts, body })
  },
  delete<T>(path: string, opts: Omit<RequestOptions, 'body'> = {}) {
    return request<T>('DELETE', path, opts)
  },
}

export const API_BASE_URL = API_URL
